import { useState, type InputHTMLAttributes } from 'react'

import { useI18n } from '@/shared/i18n/I18nProvider'

import { Icon } from './icons'

/**
 * THE ONE MASKED FIELD: a password or a secret, with the eye that shows what
 * was typed.
 *
 * Every password and every provider secret in the product is typed into this,
 * so a reader who mistyped twelve characters can check them before the server
 * refuses them, and the check works the same way on the login screen, in the
 * user creator and in a connector's credentials.
 *
 * THE FIELD IS NAMED BY THE CALLER'S `<label htmlFor>` AND NEVER BY A WRAPPING
 * `<label>`. The eye is a button with its own name, and a button inside the
 * label element would have that name read into the field's — "Password Show
 * password" — so a caller that wraps its input restructures instead.
 *
 * The eye is a toggle rather than a hold: `aria-pressed` is what a screen
 * reader announces, and a press-and-hold cannot be operated from a keyboard.
 * Its name stays the same in both states, as a pressed toggle's name does.
 * A revealed value is never spell-checked, because a spell checker may send
 * what it reads to a service.
 */
export function PasswordInput({
  id,
  kind = 'password',
  className = 'field__input',
  disabled,
  ...input
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'id'> & {
  /** Required: the eye names the field it reveals through `aria-controls`. */
  id: string
  /** Which word the eye's name uses for what it reveals. */
  kind?: 'password' | 'secret'
}) {
  const { t } = useI18n()
  const [revealed, setRevealed] = useState(false)
  const toggleLabel = t(kind === 'secret' ? 'passwordInput.showSecret' : 'passwordInput.show')
  return (
    <span className="password-input">
      <input
        spellCheck={false}
        autoCapitalize="off"
        {...input}
        id={id}
        className={`${className} password-input__field`}
        type={revealed ? 'text' : 'password'}
        disabled={disabled}
      />
      <button
        type="button"
        className="button button--icon button--quiet password-input__toggle"
        aria-label={toggleLabel}
        title={toggleLabel}
        aria-controls={id}
        aria-pressed={revealed}
        disabled={disabled}
        onClick={() => setRevealed((current) => !current)}
      >
        {revealed ? <Icon name="eye-off" /> : <Icon name="eye" />}
      </button>
    </span>
  )
}
